/**
 * PlaybackError - the single error type for the playback pipeline.
 *
 * Every failure between "user clicked play" and "frames on screen" becomes one
 * of these, so the player always has a human sentence to render. PIN 7: the bug
 * we are fixing is SILENCE — a code with no message is not allowed to exist.
 *
 * The copy for the API-facing codes is verbatim from the PINS error table.
 */

/** @typedef {'geo_blocked'|'locked'|'manifest_unavailable'|'not_found'|'unsupported'|'playback_failed'} PlaybackErrorCode */

/** @type {Record<PlaybackErrorCode, string>} */
export const PLAYBACK_ERROR_MESSAGES = {
  // --- PINS error table, verbatim ---
  geo_blocked: "This video isn't available in your region.",
  locked: 'You need an active subscription to watch this.',
  manifest_unavailable: 'This video is temporarily unavailable. Please try again.',
  not_found: 'This video could not be found.',
  // --- client-side failures, which the table does not cover ---
  unsupported: "This browser can't play this video.",
  playback_failed: 'Playback stopped unexpectedly. Please try again.',
};

/**
 * An unrecognised code must still render text, so it falls back to the generic
 * "temporarily unavailable" sentence rather than producing an empty message.
 */
export class PlaybackError extends Error {
  /**
   * @param {PlaybackErrorCode} code
   * @param {{cause?: unknown}} [options] - `cause` keeps the underlying HTTP /
   *   hls.js error for logging. It is never shown to the user.
   */
  constructor(code, options = {}) {
    super(PLAYBACK_ERROR_MESSAGES[code] || PLAYBACK_ERROR_MESSAGES.manifest_unavailable);
    this.name = 'PlaybackError';
    this.code = code;
    this.cause = options.cause;
  }
}

export default PlaybackError;
